import { ActionDtoStatusEnum } from '@issuemanager/api-client';
import { useUserContext } from '@issuemanager/apps/client/contexts';
import { ActionStatus } from '@issuemanager/constants';
import { useCallback, useMemo } from 'react';
import { ActionValues } from '../../../manage-issue/action-fields';

export type ActionInfo = ActionValues & {
  canRequestReassignment: boolean;
  canStartAction: boolean;
  canFinishAction: boolean;
  canApproveAction: boolean;
};

const useActionsInfo = (actions: ActionValues[]) => {
  const { data: meData } = useUserContext();

  const getParsedAction = useCallback(
    (action: ActionValues) => {
      const isActionOwner = action.owner?.id === meData?.id;
      const isActionApprover = action.approver?.id === meData?.id;

      const canRequestReassignment = (action: ActionValues) => {
        const nonReassignableStatuses: ActionDtoStatusEnum[] = [
          ActionDtoStatusEnum.Draft,
          ActionDtoStatusEnum.Completed,
          ActionDtoStatusEnum.Discarded,
          ActionDtoStatusEnum.Pending,
        ];

        return (
          (isActionOwner || isActionApprover) &&
          !nonReassignableStatuses.includes(action.status)
        );
      };

      const canStartAction = (action: ActionValues) =>
        action.status === ActionStatus.OPEN && isActionOwner;

      const canFinishAction = (action: ActionValues) =>
        action.status === ActionStatus.IN_PROGRESS && isActionOwner;

      const canApproveAction = (action: ActionValues) =>
        action.status === ActionStatus.TO_BE_APPROVED && isActionApprover;

      return {
        ...action,
        canRequestReassignment: canRequestReassignment(action),
        canStartAction: canStartAction(action),
        canFinishAction: canFinishAction(action),
        canApproveAction: canApproveAction(action),
      };
    },
    [meData?.id],
  );

  const memoizedActions: ActionInfo[] = useMemo(
    () => actions.map((action) => getParsedAction(action)),
    [actions, getParsedAction],
  );

  return memoizedActions;
};

export default useActionsInfo;
